import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Wrench, ArrowRightLeft, Inbox, History, ChevronRight,
  Check, X, Clock, Send, User, PackageOpen,
} from "lucide-react";

const TYPE_LABELS = {
  aspiradora: "Aspiradora",
  nebulizadora: "Nebulizadora",
  termonebulizadora: "Termonebulizadora",
  maquina_vapor: "Máquina de Vapor",
  fumigadora: "Fumigadora",
  pulverizador: "Pulverizador",
  otro: "Otro",
};

const TYPE_COLORS = {
  aspiradora: "bg-blue-100 text-blue-800 border-blue-200",
  nebulizadora: "bg-purple-100 text-purple-800 border-purple-200",
  termonebulizadora: "bg-orange-100 text-orange-800 border-orange-200",
  maquina_vapor: "bg-cyan-100 text-cyan-800 border-cyan-200",
  fumigadora: "bg-green-100 text-green-800 border-green-200",
  pulverizador: "bg-amber-100 text-amber-800 border-amber-200",
  otro: "bg-zinc-100 text-zinc-800 border-zinc-200",
};

export default function TechEquipment() {
  const nav = useNavigate();
  const [tab, setTab] = useState("inventory");
  const [equipment, setEquipment] = useState([]);
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Wizard
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [destTechId, setDestTechId] = useState("");
  const [technicians, setTechnicians] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Equipment with pending outgoing transfer (non-selectable)
  const [pendingOutgoing, setPendingOutgoing] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [eqRes, pendRes, histRes, countRes] = await Promise.all([
        api.get("/equipment/my"),
        api.get("/equipment/transfers/pending"),
        api.get("/equipment/transfers/history"),
        api.get("/equipment/transfers/pending-count"),
      ]);
      setEquipment(eqRes.data || []);
      setPending(pendRes.data || []);
      setHistory(histRes.data || []);
      setPendingCount(countRes.data.count || 0);

      // Find equipment IDs with pending outgoing transfers
      const outgoing = new Set();
      (histRes.data || []).forEach(t => {
        if (t.status === "pending" && eqRes.data.some(eq => eq.id === t.equipment_id)) {
          outgoing.add(t.equipment_id);
        }
      });
      setPendingOutgoing(outgoing);
    } catch {
      toast.error("Error al cargar equipos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadTechnicians = async () => {
    try {
      const res = await api.get("/equipment/technicians");
      setTechnicians(res.data.filter(t => t.full_name !== "Test Technician"));
    } catch { /* ignore */ }
  };

  // ─── Wizard ────────────────────────────────────
  const openWizard = () => {
    setSelectedIds(new Set());
    setDestTechId("");
    setWizardStep(1);
    setWizardOpen(true);
    loadTechnicians();
  };

  const toggleEquipment = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectableEquipment = equipment.filter(eq => !pendingOutgoing.has(eq.id));

  const canProceed = () => {
    if (wizardStep === 1) return selectedIds.size > 0;
    if (wizardStep === 2) return destTechId !== "";
    return true;
  };

  const submitTransfer = async () => {
    setSubmitting(true);
    try {
      await api.post("/equipment/transfer", {
        equipment_ids: [...selectedIds],
        to_technician_id: destTechId,
      });
      toast.success("Solicitud de traslado enviada");
      setWizardOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al trasladar");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAccept = async (id) => {
    try {
      await api.post(`/equipment/transfers/${id}/accept`);
      toast.success("Equipo recibido");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const handleReject = async (id) => {
    try {
      await api.post(`/equipment/transfers/${id}/reject`);
      toast.success("Traslado rechazado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const statusBadge = (status) => {
    if (status === "pending") return <Badge className="bg-amber-100 text-amber-800 border-amber-300"><Clock className="w-3 h-3 mr-1" />Pendiente</Badge>;
    if (status === "accepted") return <Badge className="bg-green-100 text-green-800 border-green-300"><Check className="w-3 h-3 mr-1" />Aceptado</Badge>;
    if (status === "rejected") return <Badge className="bg-red-100 text-red-800 border-red-300"><X className="w-3 h-3 mr-1" />Rechazado</Badge>;
    return null;
  };

  const tabs = [
    { id: "inventory", label: "Mis Equipos", icon: Wrench },
    { id: "pending", label: "Pendientes", icon: Inbox, badge: pendingCount },
    { id: "history", label: "Historial", icon: History },
  ];

  return (
    <div className="px-4 pt-4 pb-6" data-testid="tech-equipment-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => nav("/tech")} className="p-2 -ml-2 rounded-lg hover:bg-secondary" data-testid="equipment-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
            Gestión de Equipos
          </h1>
          <p className="text-xs text-muted-foreground">Equipos asignados y traslados</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-secondary/50 rounded-xl p-1" data-testid="equipment-tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
              tab === t.id ? "bg-white shadow-sm text-black" : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`equipment-tab-${t.id}`}
          >
            <t.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{t.label}</span>
            {t.badge > 0 && (
              <span className="bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-20 rounded-xl bg-secondary animate-pulse" />)}
        </div>
      ) : (
        <>
          {/* ═══ INVENTORY ═══ */}
          {tab === "inventory" && (
            <div className="space-y-3" data-testid="equipment-inventory">
              {selectableEquipment.length > 0 && (
                <Button
                  onClick={openWizard}
                  className="w-full h-12 bg-[#FFD62B] hover:bg-[#FFD62B]/90 text-black font-semibold"
                  data-testid="new-eq-transfer-btn"
                >
                  <ArrowRightLeft className="w-5 h-5 mr-2" />
                  Trasladar Equipos
                </Button>
              )}

              {equipment.length === 0 ? (
                <Card className="p-8 text-center">
                  <PackageOpen className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">No tienes equipos asignados</p>
                </Card>
              ) : equipment.map(eq => (
                <Card key={eq.id} className="p-4" data-testid={`equipment-item-${eq.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                      <Wrench className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span className="text-sm font-semibold">{eq.name}</span>
                        {pendingOutgoing.has(eq.id) && (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">
                            <Clock className="w-2.5 h-2.5 mr-0.5" />Traslado pendiente
                          </Badge>
                        )}
                      </div>
                      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${TYPE_COLORS[eq.equipment_type] || TYPE_COLORS.otro}`}>
                        {TYPE_LABELS[eq.equipment_type] || eq.equipment_type}
                      </Badge>
                      <div className="text-xs text-muted-foreground mt-1">
                        {eq.serial_number && <span>S/N: <span className="font-mono">{eq.serial_number}</span></span>}
                        {eq.serial_number && eq.internal_code && <span className="mx-1.5">·</span>}
                        {eq.internal_code && <span>Código: <span className="font-mono">{eq.internal_code}</span></span>}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* ═══ PENDING ═══ */}
          {tab === "pending" && (
            <div className="space-y-3" data-testid="equipment-pending">
              {pending.length === 0 ? (
                <Card className="p-8 text-center">
                  <Inbox className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">No tienes traslados de equipos pendientes</p>
                </Card>
              ) : pending.map(t => (
                <Card key={t.id} className="p-4" data-testid={`pending-eq-transfer-${t.id}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                      <Send className="w-4 h-4 text-amber-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold">{t.equipment_name}</div>
                      {t.serial_number && <div className="text-xs text-muted-foreground font-mono">S/N: {t.serial_number}</div>}
                      <div className="text-xs text-muted-foreground mt-0.5">
                        De: <span className="font-medium text-foreground">{t.from_technician_name}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleAccept(t.id)}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`accept-eq-transfer-${t.id}`}
                    >
                      <Check className="w-4 h-4 mr-1" /> Recibido
                    </Button>
                    <Button
                      onClick={() => handleReject(t.id)}
                      variant="outline"
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                      data-testid={`reject-eq-transfer-${t.id}`}
                    >
                      <X className="w-4 h-4 mr-1" /> Rechazar
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {/* ═══ HISTORY ═══ */}
          {tab === "history" && (
            <div className="space-y-3" data-testid="equipment-history">
              {history.length === 0 ? (
                <Card className="p-8 text-center">
                  <History className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">Sin historial de traslados</p>
                </Card>
              ) : history.map(t => (
                <Card key={t.id} className="p-3" data-testid={`history-eq-transfer-${t.id}`}>
                  <div className="flex items-start gap-2.5">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                      t.status === "accepted" ? "bg-green-100" : t.status === "rejected" ? "bg-red-100" : "bg-amber-100"
                    }`}>
                      <ArrowRightLeft className={`w-3.5 h-3.5 ${
                        t.status === "accepted" ? "text-green-700" : t.status === "rejected" ? "text-red-700" : "text-amber-700"
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{t.equipment_name}</span>
                        {statusBadge(t.status)}
                      </div>
                      {t.serial_number && <div className="text-xs text-muted-foreground font-mono">S/N: {t.serial_number}</div>}
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {t.from_technician_name} → {t.to_technician_name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(t.created_at).toLocaleDateString("es-SV")}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* ═══ TRANSFER WIZARD ═══ */}
      <Dialog open={wizardOpen} onOpenChange={(o) => { if (!o) setWizardOpen(false); }}>
        <DialogContent className="max-w-md mx-auto" data-testid="eq-transfer-wizard">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-[#FFD62B]" />
              Trasladar Equipos
              <Badge variant="outline" className="ml-auto">Paso {wizardStep}/3</Badge>
            </DialogTitle>
            <DialogDescription>
              {wizardStep === 1 && "Selecciona los equipos a trasladar"}
              {wizardStep === 2 && "Selecciona el técnico destino"}
              {wizardStep === 3 && "Confirma el traslado"}
            </DialogDescription>
          </DialogHeader>

          <div className="py-2 space-y-3 min-h-[200px] max-h-[400px] overflow-y-auto">
            {/* Step 1: Select equipment */}
            {wizardStep === 1 && (
              <>
                {selectableEquipment.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No hay equipos disponibles para trasladar</p>
                ) : selectableEquipment.map(eq => (
                  <label
                    key={eq.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                      selectedIds.has(eq.id) ? "border-[#FFD62B] bg-[#FFD62B]/10" : "border-border hover:border-muted-foreground/30"
                    }`}
                    data-testid={`wizard-eq-option-${eq.id}`}
                  >
                    <Checkbox
                      checked={selectedIds.has(eq.id)}
                      onCheckedChange={() => toggleEquipment(eq.id)}
                      className="data-[state=checked]:bg-[#FFD62B] data-[state=checked]:border-[#FFD62B]"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{eq.name}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${TYPE_COLORS[eq.equipment_type] || TYPE_COLORS.otro}`}>
                          {TYPE_LABELS[eq.equipment_type] || eq.equipment_type}
                        </Badge>
                        {eq.serial_number && <span className="text-xs text-muted-foreground font-mono">{eq.serial_number}</span>}
                      </div>
                    </div>
                    {selectedIds.has(eq.id) && <Check className="w-5 h-5 text-[#FFD62B] shrink-0" />}
                  </label>
                ))}
                {selectableEquipment.length > 1 && (
                  <div className="text-xs text-muted-foreground text-center pt-1">
                    {selectedIds.size} equipo{selectedIds.size !== 1 ? "s" : ""} seleccionado{selectedIds.size !== 1 ? "s" : ""}
                  </div>
                )}
              </>
            )}

            {/* Step 2: Select technician */}
            {wizardStep === 2 && (
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider mb-2">Técnico destino</div>
                <Select value={destTechId} onValueChange={setDestTechId}>
                  <SelectTrigger className="h-11" data-testid="wizard-eq-tech-select">
                    <SelectValue placeholder="Seleccionar técnico..." />
                  </SelectTrigger>
                  <SelectContent>
                    {technicians.map(t => (
                      <SelectItem key={t.id} value={t.id} data-testid={`wizard-eq-tech-${t.id}`}>
                        {t.full_name} ({t.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 mt-3">
                  El técnico destino deberá confirmar la recepción del equipo.
                </div>
              </div>
            )}

            {/* Step 3: Confirm */}
            {wizardStep === 3 && (
              <div className="space-y-3">
                <div className="rounded-xl border p-4 space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Equipos a trasladar</div>
                  {[...selectedIds].map(id => {
                    const eq = equipment.find(e => e.id === id);
                    if (!eq) return null;
                    return (
                      <div key={id} className="flex items-center gap-2 py-1.5 border-b last:border-0">
                        <Wrench className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{eq.name}</div>
                          {eq.serial_number && <div className="text-xs text-muted-foreground font-mono">S/N: {eq.serial_number}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2 text-sm px-1">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Destino:</span>
                  <span className="font-medium">{technicians.find(t => t.id === destTechId)?.full_name}</span>
                </div>
                <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-900">
                  Los equipos quedarán pendientes hasta que el técnico destino confirme la recepción.
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-row gap-2">
            {wizardStep > 1 && (
              <Button
                variant="outline"
                onClick={() => setWizardStep(s => s - 1)}
                className="flex-1"
                data-testid="wizard-eq-back-btn"
              >
                Atrás
              </Button>
            )}
            {wizardStep < 3 ? (
              <Button
                onClick={() => setWizardStep(s => s + 1)}
                disabled={!canProceed()}
                className="flex-1 bg-[#FFD62B] hover:bg-[#FFD62B]/90 text-black font-semibold"
                data-testid="wizard-eq-next-btn"
              >
                Siguiente <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={submitTransfer}
                disabled={submitting}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold"
                data-testid="wizard-eq-confirm-btn"
              >
                {submitting ? "Enviando..." : "Enviar solicitud"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
